import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarX, Plus, Trash2, Pencil, Loader2, AlertTriangle, X, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtTimeRange } from "@/lib/format";

const fld = "rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white";
const fmtRange = (b) => b.start_date === b.end_date ? b.start_date : `${b.start_date} → ${b.end_date}`;

export default function EnhancerBlackouts() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["enh-blackouts"],
    queryFn: () => api.get("/enhancer/me/blackouts").then((r) => r.data),
  });
  const { data: locs } = useQuery({
    queryKey: ["enh-schedulable-locations"],
    queryFn: () => api.get("/enhancer/me/schedulable-locations").then((r) => r.data),
  });
  const locations = locs?.locations || [];
  const [form, setForm] = useState({ location_id: "", start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [affected, setAffected] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["enh-blackouts"] });

  const create = async () => {
    if (!form.start_date) { toast.error("Pick a start date."); return; }
    setBusy(true);
    try {
      const r = await api.post("/enhancer/me/blackouts", form);
      toast.success("Dates blocked");
      setAffected(r.data.affected || []);
      setForm({ location_id: "", start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/enhancer/me/blackouts/${editing.id}`, editing);
      toast.success("Block updated");
      setAffected(r.data.affected || []);
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enhancer/me/blackouts/${id}`);
      toast.success("Block removed");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const LocSelect = ({ value, onChange, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={fld}>
      <option value="">All my locations</option>
      {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type === "own" ? " (my location)" : ""}</option>)}
    </select>
  );

  const list = data?.blackouts || [];

  return (
    <div className="mt-10 max-w-2xl" data-testid="enh-blackouts-panel">
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-1"><CalendarX size={18} className="text-[#D98E73]" /> Block me at a center</h3>
      <p className="text-xs text-[#4A5D6B] mb-3">Block yourself off at one specific center on chosen dates — other enhancers there stay bookable, and your other locations aren't affected. Existing appointments are never auto-cancelled; you'll be notified to handle them.</p>

      <div className="eyw-card p-4 mb-4" data-testid="enh-blackout-add-form">
        <div className="flex flex-wrap items-center gap-2">
          <LocSelect value={form.location_id} onChange={(v) => setForm({ ...form, location_id: v })} testid="enh-blackout-loc" />
          <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="enh-blackout-start" className={fld} />
          <span className="text-sm text-[#4A5D6B]">to</span>
          <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="enh-blackout-end" className={fld} />
          <input type="text" placeholder="Reason (optional, internal only)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="enh-blackout-reason" className={`${fld} flex-1 min-w-[150px]`} />
          <button onClick={create} disabled={busy} data-testid="enh-blackout-create-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Block
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="text-xs text-[#4A5D6B]">Hours (optional):</span>
          <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="enh-blackout-start-time" className={fld} />
          <span className="text-sm text-[#4A5D6B]">–</span>
          <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="enh-blackout-end-time" className={fld} />
          {(form.start_time || form.end_time) && (
            <button type="button" onClick={() => setForm({ ...form, start_time: "", end_time: "" })} data-testid="enh-blackout-clear-times" className="text-xs text-[#8FA89B] underline">Clear (block whole day)</button>
          )}
        </div>
        <p className="text-[11px] text-[#8FA89B] mt-2">Set hours to block only part of the day — you stay bookable outside them.</p>
      </div>

      {affected && affected.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="enh-blackout-affected-warning">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={15} /> {affected.length} existing appointment{affected.length > 1 ? "s" : ""} fall on those dates</p>
          <p className="text-xs text-amber-700 mt-1 mb-2">They were <b>not</b> cancelled — reschedule or cancel them from your Bookings tab.</p>
          <ul className="space-y-1">
            {affected.map((a) => (
              <li key={a.id} className="text-xs text-amber-800" data-testid={`enh-blackout-affected-${a.id}`}>{a.date} {a.start_time} · {a.service_title} — {a.client}</li>
            ))}
          </ul>
          <button onClick={() => setAffected(null)} className="text-[11px] text-amber-700 underline mt-2" data-testid="enh-blackout-affected-dismiss">Dismiss</button>
        </div>
      )}

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : (
        <div className="space-y-2" data-testid="enh-blackout-list">
          {list.length === 0 && <p className="text-sm text-[#8FA89B]">No center blocks.</p>}
          {list.map((b) => (
            <div key={b.id} data-testid={`enh-blackout-row-${b.id}`} className="eyw-card p-3.5">
              {editing?.id === b.id ? (
                <div className="flex flex-wrap items-center gap-2">
                  <LocSelect value={editing.location_id} onChange={(v) => setEditing({ ...editing, location_id: v })} testid={`enh-blackout-edit-loc-${b.id}`} />
                  <input type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} data-testid={`enh-blackout-edit-start-${b.id}`} className={fld} />
                  <span className="text-sm text-[#4A5D6B]">to</span>
                  <input type="date" value={editing.end_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} data-testid={`enh-blackout-edit-end-${b.id}`} className={fld} />
                  <input type="time" value={editing.start_time} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} data-testid={`enh-blackout-edit-start-time-${b.id}`} className={fld} />
                  <span className="text-sm text-[#4A5D6B]">–</span>
                  <input type="time" value={editing.end_time} onChange={(e) => setEditing({ ...editing, end_time: e.target.value })} data-testid={`enh-blackout-edit-end-time-${b.id}`} className={fld} />
                  <input type="text" placeholder="Reason (internal)" value={editing.reason} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} className={`${fld} flex-1 min-w-[120px]`} />
                  <button onClick={saveEdit} disabled={busy} data-testid={`enh-blackout-save-${b.id}`} className="text-[#5C8A72]"><Check size={17} /></button>
                  <button onClick={() => setEditing(null)} className="text-[#8FA89B]"><X size={17} /></button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#1A242B]"><MapPin size={13} className="text-[#144F75]" /> {b.location_name || "All my locations"}</span>
                  <span className="text-sm text-[#4A5D6B]">{fmtRange(b)}</span>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${b.start_time ? "bg-[#144F75]/[0.08] text-[#144F75]" : "bg-red-50 text-red-500"}`} data-testid={`enh-blackout-hours-${b.id}`}>{b.start_time ? `${fmtTimeRange(b.start_time, b.end_time)}` : "All day"}</span>
                  {b.reason && <span className="text-xs text-[#4A5D6B] bg-[#F1EDE6] rounded-full px-2 py-0.5">{b.reason}</span>}
                  <span className="ml-auto flex items-center gap-2.5">
                    <button onClick={() => setEditing({ id: b.id, location_id: b.location_id || "", start_date: b.start_date, end_date: b.end_date, start_time: b.start_time || "", end_time: b.end_time || "", reason: b.reason || "" })} data-testid={`enh-blackout-edit-${b.id}`} className="text-[#144F75]"><Pencil size={15} /></button>
                    <button onClick={() => remove(b.id)} data-testid={`enh-blackout-delete-${b.id}`} className="text-red-500"><Trash2 size={15} /></button>
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
